<template>
  <div
    role="region"
    aria-label="Item details"
    @keydown="handleKeyDown"
    tabindex="-1"
    ref="detailsRegion"
  >
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary mb-2"
      @click="$emit('back')"
      aria-label="Return to navigation list"
      ref="backButton"
    >
      <i class="bi bi-arrow-left me-1" aria-hidden="true"></i>Back to list
    </button>
    <div
      v-for="(config, index) in displayConfig"
      :key="index"
      :class="getFieldClass(config.component)"
    >
      <template v-if="shouldShowField(config.field)">
        <h5
          v-if="config.component === 'title' && showTitle"
          class="text-dark mb-2 h4"
        >
          {{ getFieldValue(config.field) }}
        </h5>

        <p
          v-else-if="config.component === 'subtitle'"
          class="text-muted d-block mb-3 h5"
          role="doc-subtitle"
        >
          {{ getFieldValue(config.field) }}
        </p>

        <p v-else-if="config.component === 'text'" class="my-3">
          {{ getFieldValue(config.field) }}
        </p>

        <section v-else-if="config.component === 'section'">
          <hr class="my-4" aria-hidden="true" />
          <h6 class="text-muted mt-3 mb-2 fs-6">{{ config.label }}</h6>
          <p class="fw-bold">{{ getFieldValue(config.field) }}</p>
        </section>

        <div v-else-if="config.component === 'field'">
          <hr class="my-4" />
          <strong>{{ config.label }}:</strong>
          {{ getFieldValue(config.field) }}
        </div>

        <div v-else-if="config.component === 'image'">
          <hr class="my-4" />
          <h6>{{ config.label }}</h6>
          <img
            :src="getImagePath(getFieldValue(config.field))"
            :alt="getImageAltText(getFieldValue(config.field), config.label)"
            class="img-fluid rounded mb-2"
            @error="handleImageError"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getDisplayConfig } from "@/helpers/displayConfig.js";

export default {
  name: "SelectionDetails",
  props: {
    showTitle: {
      type: Boolean,
      default: true
    }
  },
  emits: ["back"],
  computed: {
    ...mapGetters({
      selectedItem: "selectedItem"
    }),
    displayConfig() {
      if (!this.selectedItem || !this.selectedItem.type) {
        return [];
      }
      return getDisplayConfig(this.selectedItem.type);
    }
  },
  methods: {
    getFieldValue(field) {
      if (!this.selectedItem || !field) {
        return "";
      }
      return this.selectedItem[field] || "";
    },
    shouldShowField(field) {
      const value = this.getFieldValue(field);
      return value !== "" && value !== null && value !== undefined;
    },
    getFieldClass(component) {
      if (component === "section" || component === "image") {
        return "mb-4";
      }
      if (component === "field") {
        return "mb-2";
      }
      return "";
    },
    // currently not used, but might incorporate later
    getImagePath(photographField) {
      if (!photographField) {
        return "";
      }
      const match = photographField.match(/^"([^"]+)"/);
      return match ? match[1] : photographField;
    },
    /**
     * Generates accessible alt text for images
     * @param {String} photographField - The photograph field value (may contain caption)
     * @param {String} label - The label for the image section
     * @returns {String} Accessible alt text
     */
    getImageAltText(photographField, label) {
      if (!photographField) {
        return label || "Image";
      }
      // Extract caption if present (format: "path/to/image.jpg" Caption text)
      const captionMatch = photographField.match(/"([^"]+)"\s*(.+)?/);
      if (captionMatch && captionMatch[2]) {
        return captionMatch[2].trim();
      }
      // If no caption, use the label and item name
      const itemName = this.selectedItem?.Name || this.selectedItem?.name || "";
      return itemName
        ? `${label || "Image"} for ${itemName}`
        : label || "Image";
    },
    handleImageError(event) {
      event.target.style.display = "none";
    },
    /**
     * Handles keyboard navigation for the details panel
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.$emit("back");
      }
    },
    /**
     * Handles document-level keyboard events (for Escape key)
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleDocumentKeyDown(event) {
      if (event.key === "Escape") {
        // Only handle if not in an input/textarea/select
        const target = event.target;
        if (
          target.tagName !== "INPUT" &&
          target.tagName !== "TEXTAREA" &&
          target.tagName !== "SELECT"
        ) {
          event.preventDefault();
          this.$emit("back");
        }
      }
    }
  },
  mounted() {
    // Focus the back button when component mounts for keyboard users
    this.$nextTick(() => {
      if (this.$refs.backButton) {
        this.$refs.backButton.focus();
      }
    });
    // Add document-level Escape key handler
    document.addEventListener("keydown", this.handleDocumentKeyDown);
  },
  beforeUnmount() {
    // Remove document-level Escape key handler
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
  }
};
</script>

<style scoped>
/* All styling is handled by Bootstrap utility classes */
</style>
